// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOrganization, TySubscription } from "@/types";
import { formatMoney, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import SelectInput from "@/UI/inputs/SelectInput";
import StepperInput from "@/UI/inputs/StepperInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Types --------------------------------------------------------------------------------------
export type TySubscriptionFormDetails = {
    /** Pre-chosen when the form is opened from a company's own screen; absent on the billing queue. */
    organization?: { _id: string; name: TyOrganization["name"]; slug: string } | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * Records a subscription **request** — never a live period. Billing is manual, so the money is
 * confirmed by a person afterwards and that is what starts the clock.
 *
 * **This is also the renewal form.** There is no separate renew: a company with a live period gets a
 * second request, and confirming it queues the new period behind the current one. Nothing in this
 * product ever edits a period that has already been paid for.
 *
 * The amount is worked out from the plan price and the addons and left editable, because `price` is
 * what was actually charged and a discount is a real thing — `basePrice` keeps the list price beside
 * it, so the row still says what it was sold at.
 */
export default function SubscriptionFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { organization: preset, onDone } = (details ?? {}) as TySubscriptionFormDetails;

    const [organization, setOrganization] = useState(preset?._id ?? "");
    const [period, setPeriod] = useState<string>(constants.subscriptionPeriods[1]);
    const [addons, setAddons] = useState(0);
    const [price, setPrice] = useState("");
    const [note, setNote] = useState("");
    const [formError, setFormError] = useState<string | null>(null);

    /** Only asked for when there is a choice to make: a company's own screen has already made it. */
    const companies = useFetch<{ organizations: TyOrganization[] }>("/organizations", {}, !preset);
    const save = useFetch<{ subscription: TySubscription }>("/subscriptions", { method: "POST" }, false);

    useEffect(() => {
        if (!preset) companies.invoke({ query: { limit: constants.maxPageSize } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!save.succeededAt) return;
        onDone?.();
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    /** What the row will be sold at unless somebody types over it. */
    const addonCount = addons;
    const suggested = constants.subscriptionPrices[period] + addonCount * constants.storageAddon[period as "quarterly" | "annually"];

    const handleSubmit = () => {
        setFormError(null);
        if (!organization) return setFormError(t.subscriptions.fields.organization);

        const asked = price.trim() === "" ? suggested : Number(price);
        if (!Number.isFinite(asked) || asked < 0) return setFormError(t.subscriptions.fields.price);

        save.invoke({ method: "POST", data: { organization, period, addons: addonCount, price: asked, note: note.trim() || null } });
    };

    const options = (companies.data?.organizations ?? []).map((company) => ({
        value: company._id,
        label: readLangValue(company.name, lang) || company.slug,
    }));

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{preset ? t.subscriptions.renewTitle : t.subscriptions.createTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {preset ? (
                    <div className="side-form__fact">
                        <span>{t.subscriptions.fields.organization}</span>
                        <strong>{readLangValue(preset.name, lang) || preset.slug}</strong>
                    </div>
                ) : (
                    <>
                        <SelectInput
                            value={organization}
                            onChange={setOrganization}
                            options={options}
                            label={t.subscriptions.fields.organization}
                            disabled={save.loading || companies.loading}
                        />
                        {!companies.loading && !options.length && <p className="side-form__hint">{t.subscriptions.noOrganizations}</p>}
                    </>
                )}

                <SelectInput
                    value={period}
                    onChange={(value) => {
                        setPeriod(value);
                        // The suggestion follows the period, so a stale typed amount is cleared.
                        setPrice("");
                    }}
                    options={constants.subscriptionPeriods.map((item) => ({ value: item, label: t.subscriptions.periods[item] }))}
                    label={t.subscriptions.fields.period}
                    disabled={save.loading}
                />

                {/* No ceiling here. A dropdown of five numbers was a product rule nobody had made:
                    an admin writing this row is recording a conversation that already happened. */}
                <StepperInput
                    label={t.subscriptions.fields.addons}
                    hint={t.subscriptions.addonsHint
                        .replace("{gb}", String(constants.storageAddon.gb))
                        .replace("{price}", formatMoney(constants.storageAddon[period as "quarterly" | "annually"]))
                        .replace("{currency}", t.subscriptions.currency)}
                    value={addons}
                    onChange={(value) => {
                        setAddons(value);
                        setPrice("");
                    }}
                    disabled={save.loading}
                />

                <TextInput
                    value={price}
                    onChange={setPrice}
                    label={`${t.subscriptions.fields.price} · ${t.subscriptions.currency}`}
                    dir="ltr"
                    disabled={save.loading}
                />
                {/* The figure goes in the hint, not the placeholder: a field with a label drops its
                    placeholder — see `inputs/TextInput` — so the calculated amount was never drawn,
                    and this form's whole promise is that you can leave the box empty. */}
                <p className="side-form__hint">{t.subscriptions.priceHint.replace("{amount}", formatMoney(suggested))}</p>

                <TextInput value={note} onChange={setNote} label={t.subscriptions.fields.note} multiline rows={2} disabled={save.loading} />

                {(formError || save.error) && <p className="side-form__error">{formError ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
